import React from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import videojs from '!video.js';

// The actual plugin function is exported by this module, but it is also
// attached to the `Player.prototype`; so, there is no need to assign it
// to a variable.

// eslint-disable-next-line import/no-webpack-loader-syntax
import '!videojs-contrib-quality-levels';

// The actual plugin function is exported by this module, but it is also
// attached to the `Player.prototype`; so, there is no need to assign it
// to a variable.
// eslint-disable-next-line import/no-webpack-loader-syntax
import qualitySelectorHls from '!videojs-quality-selector-hls';

import { isMobile } from './utils/is-mobile';

export default class ReactVideoJS extends React.Component {
  constructor(props) {
    super(props);

    this.isSeeking = false;
    this.maxWatchedTime = 0;
    this.lastNaturalTime = 0; 
    this.isDragging = false;
    this.dragStartTime = 0;

    this.state = {
      showPreventSeekForwardMessage: false,
      progressOverlayStyle: {},
    };
  }

  componentDidMount() {
    const { addStartedClass } = this.props;
    const { addQualitySelector } = this.props;
    const { preventSeekForward } = this.props;

    videojs.registerPlugin('qualitySelectorHls', qualitySelectorHls);

    /**
     * Get the ReactVideoJs class
     */
    const self = this;

    // instantiate Video.js
    this.player = videojs(this.videoNode, this.props, function onPlayerReady() {
      let videoId = null;
      window.parent.postMessage({ name: 'video-playback-available', url: document.location.href }, '*');

      try {
        // Store in reverse order, just to not allow easy reusing of the ID
        videoId = this.currentSource().src.split('/')[4].split('_')[1].split('.')[0].split('').reverse().join('');
      } catch (e) {}

      if (videoId) {
        /* Keyed by viewer when there is one, so another account on this browser starts the video
           from the beginning. A position saved before viewers existed goes to the first one to
           open the video here, so nobody loses where they were. */
        const storageKey = self.props.viewer ? `${self.props.viewer}-${videoId}` : videoId;
        if (storageKey !== videoId) {
          ['position', 'max-watched'].forEach((suffix) => {
            const saved = localStorage.getItem(`${videoId}-${suffix}`);
            if (saved === null) return;
            if (localStorage.getItem(`${storageKey}-${suffix}`) === null) {
              localStorage.setItem(`${storageKey}-${suffix}`, saved);
            }
            localStorage.removeItem(`${videoId}-${suffix}`);
          });
        }

        const lastPosition = localStorage.getItem(`${storageKey}-position`);

        // Restore previously saved position
        if (lastPosition) {
          this.currentTime(parseFloat(lastPosition));
        }

        const storedMaxWatched = localStorage.getItem(`${storageKey}-max-watched`);
        self.maxWatchedTime = storedMaxWatched ? parseFloat(storedMaxWatched) : 0;
        self.lastNaturalTime = self.maxWatchedTime;

        // Persist position
        this.on('timeupdate', function (e) {
          const currentTime = this.currentTime();

          localStorage.setItem(`${storageKey}-position`, currentTime);
        
          if (!self.isSeeking && currentTime > self.lastNaturalTime) {
            
            /**
             * Check if the current time is within 2 seconds of the last natural time, to avoid seeking issues
             */
            const timeDiff = Math.abs(currentTime - self.lastNaturalTime);

            /**
             * If the time difference is less than 2 seconds, we consider it a natural playback
             */
            if (timeDiff < 2) {
              if (currentTime > self.maxWatchedTime) {
                self.maxWatchedTime = currentTime;
                localStorage.setItem(`${storageKey}-max-watched`, self.maxWatchedTime);
              }
              self.lastNaturalTime = currentTime;
            }
          }

          if (self.props.preventSeekForward) {
            self.updateProgressOverlay();
          }

          // Last 30 seconds
          if (this.duration() - currentTime < 30) {
            if (!window.playbackCompletionNotified) {
              window.playbackCompletionNotified = true;
              window.parent.postMessage({ name: 'video-playback-completed', url: document.location.href }, '*');
            }
          }
        });
      } else {
        console.error('Error parsing video ID - this can affect the tracking');
      }

      const qualityLevels = this.qualityLevels();

      qualityLevels.on('addqualitylevel', function () {
        for (let i = 0; i < qualityLevels.length; i++) {
          const level = qualityLevels[i];
          level.enabled = level.height === 720;
        }
      });

      if (addStartedClass) {
        this.addClass('vjs-has-started');
      }
    });

    if (addQualitySelector) {
      this.player.qualitySelectorHls();
      this.player.controlBar.show();
    }

    if (preventSeekForward) {
      this.initializeSeekPrevention();
    }

    /**
     * Check resize to update progress overlay position
     */
    this.handleResize = () => {
      if (this.props.preventSeekForward) {
        this.updateProgressOverlay();
      }
    };

    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    if (this.player) this.player.dispose();
    if (this.handleResize) window.removeEventListener('resize', this.handleResize);
  }

  /**
   * Update progress overlay position based on max watched time
   */
  updateProgressOverlay() {
    if (!this.player || !this.player.duration()) return;

    const duration = this.player.duration();
    const watchedPercentage = (this.maxWatchedTime / duration) * 100;
    
    if (!isMobile()) {
      const progressControl = this.player.el().querySelector('.vjs-progress-control');
      if (progressControl) {
        const progressBar = progressControl.querySelector('.vjs-progress-holder');
        if (progressBar) {
          const progressBarRect = progressBar.getBoundingClientRect();
          const playerRect = this.player.el().getBoundingClientRect();
          
          const progressBarLeft = progressBarRect.left - playerRect.left;
          const progressBarWidth = progressBarRect.width;
          const overlayLeft = progressBarLeft + (progressBarWidth * watchedPercentage / 100);
          const overlayWidth = progressBarWidth * (100 - watchedPercentage) / 100;
          
          this.setState({
            progressOverlayStyle: {
              left: `${overlayLeft}px`,
              width: `${overlayWidth}px`,
            }
          });
        }
      }
    }
  }

  /**
   * Initialize drag prevention on progress bar 
   */
  initializeDragPrevention() {
    const progressHolder = this.player.el().querySelector('.vjs-progress-holder');
    if (!progressHolder) return;

    const self = this;

    progressHolder.addEventListener('mousedown', function(e) {
      if (!self.props.preventSeekForward) return;

      const duration = self.player.duration();
      if (!duration) return;

      const rect = progressHolder.getBoundingClientRect();
      const clickX = e.clientX - rect.left;
      const clickPercentage = (clickX / rect.width) * 100;
      const clickTime = (clickPercentage / 100) * duration;

      if (clickTime > self.maxWatchedTime) {
        e.preventDefault();
        e.stopPropagation();
        self.handleBlockOverlayMessage();
        return false;
      }

      self.isDragging = true;
      self.dragStartTime = clickTime;

      const handleMouseMove = function(moveEvent) {
        if (!self.isDragging) return;

        const moveRect = progressHolder.getBoundingClientRect();
        const moveX = moveEvent.clientX - moveRect.left;
        const movePercentage = Math.max(0, Math.min(100, (moveX / moveRect.width) * 100));
        const moveTime = (movePercentage / 100) * duration;

        if (moveTime > self.maxWatchedTime) {
          self.isDragging = false;
          
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
          
          self.player.currentTime(self.maxWatchedTime);
          
          const mouseUpEvent = new MouseEvent('mouseup', {
            bubbles: true,
            cancelable: true,
            clientX: moveEvent.clientX,
            clientY: moveEvent.clientY
          });
          progressHolder.dispatchEvent(mouseUpEvent);
          
          self.handleBlockOverlayMessage();
          
          return;
        }
      };

      const handleMouseUp = function() {
        self.isDragging = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }, true);
  }

  /**
   * Prevent seeking past the maximum watched time
   */
  initializeSeekPrevention() {
    this.player.on('seeking', () => {
      this.isSeeking = true;
    });

    this.player.on('seeked', () => {
      const currentTime = this.player.currentTime();

      if (this.props.preventSeekForward && currentTime > this.maxWatchedTime) {
        this.player.currentTime(this.maxWatchedTime);
        this.handleBlockOverlayMessage();
      }

      setTimeout(() => {
        this.isSeeking = false
        this.lastNaturalTime = this.player.currentTime();
      }, 300);
    });

    setTimeout(() => {
      this.updateProgressOverlay();
      this.initializeDragPrevention();
    }, 100);
  }

  /**
   * Show not allowed seek message
   */
  handleBlockOverlayMessage = (e) => {
    if (e) e.stopPropagation();

    let showAlertInsteadOfToast = false;

    if(document.fullscreenElement) {
      try {
        document.exitFullscreen();
      } catch (e) {
        showAlertInsteadOfToast = true;
      }
    }

    if (!showAlertInsteadOfToast) {
      if (this.state.showPreventSeekForwardMessage) return;

      this.setState({ showPreventSeekForwardMessage: true });
      setTimeout(() => {
        this.setState({ showPreventSeekForwardMessage: false });
      }, 3000);
    }

    else alert(this.props.preventSeekForwardMessage)
  }


  // wrap the player in a div with a `data-vjs-player` attribute
  // so videojs won't create additional wrapper in the DOM
  // see https://github.com/videojs/video.js/pull/3856
  render() {
    if (this.props.isVideo) {
      return (
        <div style={{ width: '100vw', height: '100vh' }}>
          {this.state.showPreventSeekForwardMessage && (
            <div className="prevent-seek-toast">
              <p>{this.props.preventSeekForwardMessage}</p>
            </div>
          )}
          <div data-vjs-player style={{ position: 'relative' }}>
            <video
              ref={node => (this.videoNode = node)}
              className={`video-js vjs-fill vjs-big-play-centered ${
                !isMobile() ? 'vjs-show-big-play-button-on-pause' : 'is-mobile'
              }`}></video>
            {(this.props.preventSeekForward && !isMobile()) && (
              <div 
                className="progress-block-overlay"
                style={this.state.progressOverlayStyle}
                onClick={this.handleBlockOverlayMessage}
              />
            )}
          </div>
        </div>
      );
    } else {
      return (
        <div style={{ width: '100vw', height: '45px' }}>
          <div data-vjs-player>
            <audio ref={node => (this.videoNode = node)} className="video-js vjs-fill"></audio>
          </div>
        </div>
      );
    }
  }
}